/**
 * Gate 2 — Decision step (G2-E4). Layout/tokens from Figma node 5503:28861.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { ReactComponent as CheckBoxUncheckedIcon } from "../../asserts/logo/Check_box.svg";
import { ReactComponent as SuccessWarningIcon } from "../../asserts/logo/SuccessWarning.svg";
import { ReactComponent as WarningTriangleIcon } from "../../asserts/logo/WarningTraingleIcon.svg";
import { GATE2_CHECKBOX_FOCUS_SX } from "./gate2ScopeTableStyles";
import { GATE2_FOCUS_RING_SX } from "./gate2Tokens";

const DECISION_CHECKBOX_SX = {
  ...GATE2_CHECKBOX_FOCUS_SX,
  p: 0,
  width: 24,
  height: 24,
  "& .MuiSvgIcon-root": { fontSize: 24 },
};

const DECISION_CHECKBOX_ICON_PROPS = { style: { width: 24, height: 24, display: "block" } };

function DecisionCheckBoxCheckedIcon(props) {
  return (
    <svg width="24" height="24" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <rect x="0.5" y="0.5" width="17" height="17" rx="3.5" fill="#005BF0" stroke="#005BF0" />
      <path
        d="M5.25 9L7.75 11.5L12.75 6.5"
        stroke="#FFFFFF"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Gate2DecisionCheckbox({ sx, ...props }) {
  return (
    <Checkbox
      size="small"
      disableRipple
      icon={<CheckBoxUncheckedIcon {...DECISION_CHECKBOX_ICON_PROPS} />}
      checkedIcon={<DecisionCheckBoxCheckedIcon {...DECISION_CHECKBOX_ICON_PROPS} />}
      sx={{ ...DECISION_CHECKBOX_SX, ...sx }}
      {...props}
    />
  );
}

const RATIONALE_MAX = 1000;

/** Figma 5503 — same white surface as Quality Scorecard main panel. */
const DECISION_SURFACE_CARD_SX = {
  border: "1px solid #E6E6E6",
  borderRadius: "12px",
  bgcolor: "#FFFFFF",
  p: { xs: 2, md: 3 },
  boxSizing: "border-box",
};

const DECISION_RESULTS_PANEL_SX = {
  border: "1px solid #E6E6E6",
  borderRadius: "12px",
  bgcolor: "#FFFFFF",
  p: { xs: 2, md: 2.5 },
  display: "flex",
  flexDirection: "column",
  gap: 2.5,
  boxSizing: "border-box",
};

const VOTE_OPTIONS = [
  {
    value: "go",
    title: "Go",
    description: "Available when no active gaps remain.",
  },
  {
    value: "conditional_go",
    title: "Conditional Go",
    description: "Proceed under logged conditions.",
  },
  {
    value: "no_go",
    title: "No-Go",
    description: "Do not proceed because the remaining risk is unacceptable.",
  },
];

function VoteOptionRow({ option, selected, disabled, onSelect }) {
  const active = selected === option.value;
  return (
    <Box
      component="button"
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      onClick={() => !disabled && onSelect(option.value)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        width: "100%",
        textAlign: "left",
        p: 2,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: active ? "#005BF0" : "#E6E6E6",
        bgcolor: active ? "#F0F6FF" : "#FFFFFF",
        boxShadow: active ? "0px 2px 4px rgba(0, 91, 240, 0.08)" : "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        boxSizing: "border-box",
        "&:focus": { outline: "none" },
        "&:focus-visible:not(:disabled)": {
          ...GATE2_FOCUS_RING_SX,
          borderColor: "#005BF0",
        },
      }}
    >
      <Radio
        checked={active}
        disabled={disabled}
        value={option.value}
        tabIndex={-1}
        aria-hidden
        sx={{
          p: 0,
          width: 24,
          height: 24,
          color: "#707070",
          "&.Mui-checked": { color: "#005BF0" },
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontSize: 14,
            fontWeight: 700,
            lineHeight: "21px",
            color: active ? "#005BF0" : "#383838",
          }}
        >
          {option.title}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontSize: 14,
            fontWeight: 400,
            lineHeight: "21px",
            color: "#555555",
            mt: 0.5,
          }}
        >
          {option.description}
        </Typography>
      </Box>
    </Box>
  );
}

function formatTargetThreshold(targetTrustIndex) {
  if (targetTrustIndex == null || !Number.isFinite(Number(targetTrustIndex))) return null;
  const n = Number(targetTrustIndex);
  const label = n % 1 === 0 ? String(Math.round(n)) : String(n);
  return `>${label}%`;
}

function buildQualityThresholdSummary({
  metricsPassedCount,
  metricsTotal,
  customPassedCount,
  customTotal,
  allPassed,
}) {
  const mTotal = metricsTotal || 0;
  const cTotal = customTotal || 0;
  if (allPassed && (mTotal > 0 || cTotal > 0)) {
    const mPart =
      mTotal > 0
        ? `All ${mTotal} standard metric${mTotal === 1 ? "" : "s"}`
        : "";
    const cPart =
      cTotal > 0 ? `all ${cTotal} test case${cTotal === 1 ? "" : "s"}` : "";
    if (mPart && cPart) return `${mPart} and ${cPart} passed the quality threshold.`;
    if (mPart) return `${mPart} passed the quality threshold.`;
    if (cPart) return `All ${cTotal} test case${cTotal === 1 ? "" : "s"} passed the quality threshold.`;
  }
  const parts = [];
  if (mTotal > 0) {
    parts.push(
      `${metricsPassedCount} of ${mTotal} standard metric${mTotal === 1 ? "" : "s"}`
    );
  }
  if (cTotal > 0) {
    parts.push(
      `${customPassedCount} of ${cTotal} custom test case${cTotal === 1 ? "" : "s"}`
    );
  }
  if (!parts.length) return "Complete execution to evaluate the quality threshold.";
  return `${parts.join(" and ")} passed the quality threshold.`;
}

/** Figma Decision — radial overall score (green pass / red below target). */
function DecisionOverallScoreDonut({ score, ringSuccess }) {
  const value = score != null ? Math.min(100, Math.max(0, Number(score))) : null;
  const size = 132;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = value != null ? (value / 100) * circumference : 0;
  const ringColor = value == null ? "#CBD5E1" : ringSuccess ? "#00754E" : "#C01919";

  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E6E6E6"
          strokeWidth={stroke}
        />
        {value != null ? (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={ringColor}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${progress} ${circumference - progress}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        ) : null}
      </svg>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontSize: 28,
            fontWeight: 900,
            lineHeight: 1,
            color: "#383838",
          }}
        >
          {value != null ? `${Math.round(value)}%` : "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function HandoffCard({ icon, title, description }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2,
        minHeight: 112,
        boxSizing: "border-box",
        bgcolor: "#FAFAFA",
        border: "1px solid #E6E6E6",
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          p: 1,
          borderRadius: "6px",
          bgcolor: "#E6E6E6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontSize: 15,
            fontWeight: 700,
            lineHeight: "normal",
            color: "#383838",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontSize: 13,
            fontWeight: 400,
            lineHeight: "normal",
            color: "#555555",
            mt: 0.5,
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

export default function Gate2DecisionStep({
  overallScore,
  targetTrustIndex,
  metricsPassedCount = 0,
  metricsFailedCount = 0,
  metricsTotal = 0,
  customFailedCount = 0,
  customTotal = 0,
  vote,
  onVoteChange,
  rationale,
  onRationaleChange,
  attestationChecked,
  onAttestationChange,
}) {
  const [successAlertOpen, setSuccessAlertOpen] = useState(true);
  const [escalationAlertOpen, setEscalationAlertOpen] = useState(true);

  const meetsTarget = useMemo(() => {
    if (overallScore == null || targetTrustIndex == null) return false;
    return overallScore >= targetTrustIndex;
  }, [overallScore, targetTrustIndex]);

  const customPassedCount = Math.max(0, customTotal - customFailedCount);
  const hasGapFailures = metricsFailedCount > 0 || customFailedCount > 0;
  const goVoteDisabled = !meetsTarget || hasGapFailures;

  const rationaleRequired =
    vote !== "go" || !meetsTarget || hasGapFailures;

  useEffect(() => {
    if (goVoteDisabled && vote === "go") {
      onVoteChange("conditional_go");
    }
  }, [goVoteDisabled, vote, onVoteChange]);
  const qualityGatePassed = meetsTarget && !hasGapFailures && overallScore != null;
  const showSuccessAlert = qualityGatePassed && successAlertOpen;
  const showEscalationAlert = hasGapFailures && escalationAlertOpen;
  const targetThresholdLabel = formatTargetThreshold(targetTrustIndex);
  const scoreDeviationBelow =
    overallScore != null && targetTrustIndex != null && overallScore < targetTrustIndex
      ? Math.max(0, Math.round(Number(targetTrustIndex) - Number(overallScore)))
      : null;
  const thresholdSummary = buildQualityThresholdSummary({
    metricsPassedCount,
    metricsTotal,
    customPassedCount,
    customTotal,
    allPassed: !hasGapFailures && overallScore != null,
  });

  const escalationMessage = useMemo(() => {
    const parts = [];
    if (metricsTotal > 0) {
      parts.push(
        `${metricsFailedCount} of ${metricsTotal} metric${metricsTotal === 1 ? "" : "s"}`
      );
    }
    if (customTotal > 0) {
      parts.push(
        `${customFailedCount} of ${customTotal} custom test case${customTotal === 1 ? "" : "s"}`
      );
    }
    if (!parts.length) return "A single No-Go vote triggers mandatory escalation.";
    return `A single No-Go vote triggers mandatory escalation. ${parts.join(" and ")} are below target.`;
  }, [metricsFailedCount, metricsTotal, customFailedCount, customTotal]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        px: { xs: 2, md: 3 },
        py: 2.5,
        bgcolor: "#FAFAFA",
      }}
    >
      <Box sx={{ ...DECISION_SURFACE_CARD_SX, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Typography
            component="h2"
            id="gate2-decision-heading"
            sx={{
              fontFamily: "Lato, sans-serif",
              fontSize: 20,
              fontWeight: 700,
              lineHeight: "28px",
              color: "#0F172A",
            }}
          >
            Decision
          </Typography>
          <Typography
            component="p"
            sx={{
              fontFamily: "Lato, sans-serif",
              fontSize: { xs: 13, sm: 14 },
              fontWeight: 400,
              lineHeight: "20px",
              color: "#64748B",
              mt: 1,
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflowWrap: { xs: "break-word", md: "normal" },
              wordBreak: { xs: "break-word", md: "normal" },
            }}
          >
            Review the quality gate results and cast your Go, Conditional Go, or No-Go vote. Your decision
            is final once submitted.
          </Typography>
        </Box>

        <Box sx={DECISION_RESULTS_PANEL_SX}>
          {showSuccessAlert ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                width: "100%",
                minWidth: 360,
                minHeight: 64,
                p: "16px",
                borderRadius: "8px",
                bgcolor: "#E8FCF3",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              <Box sx={{ width: 32, height: 32, flexShrink: 0, display: "flex", alignItems: "center" }}>
                <SuccessWarningIcon style={{ width: 32, height: 32, display: "block" }} />
              </Box>
              <Typography
                sx={{
                  flex: 1,
                  fontFamily: "Lato, sans-serif",
                  fontSize: 14,
                  fontWeight: 400,
                  fontStyle: "normal",
                  lineHeight: "21px",
                  letterSpacing: 0,
                  color: "#00754E",
                  verticalAlign: "middle",
                }}
              >
                Quality gate passed — all metrics and test cases meet the required thresholds.
              </Typography>
              <IconButton
                size="small"
                onClick={() => setSuccessAlertOpen(false)}
                aria-label="Dismiss success alert"
                sx={{ flexShrink: 0, color: "#707070" }}
              >
                <CloseIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Box>
          ) : null}

          {showEscalationAlert ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                width: "100%",
                minWidth: 360,
                minHeight: 64,
                p: "16px",
                borderRadius: "8px",
                bgcolor: "#FFF8E0",
                boxSizing: "border-box",
                overflow: "hidden",
              }}
            >
              <Box sx={{ width: 32, height: 32, flexShrink: 0, display: "flex", alignItems: "center" }}>
                <WarningTriangleIcon style={{ width: 32, height: 32, display: "block" }} />
              </Box>
              <Typography
                sx={{
                  flex: 1,
                  fontFamily: "Lato, sans-serif",
                  fontSize: 14,
                  fontWeight: 400,
                  fontStyle: "normal",
                  lineHeight: "21px",
                  letterSpacing: 0,
                  color: "#8A5C00",
                  verticalAlign: "middle",
                }}
              >
                {escalationMessage}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setEscalationAlertOpen(false)}
                aria-label="Dismiss alert"
                sx={{ flexShrink: 0, color: "#707070" }}
              >
                <CloseIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Box>
          ) : null}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 1.5,
              textAlign: "left",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontSize: 14,
                fontWeight: 700,
                color: "#383838",
              }}
            >
              Overall Score
            </Typography>

            <DecisionOverallScoreDonut
              score={overallScore}
              ringSuccess={meetsTarget && !hasGapFailures}
            />

            {overallScore != null && targetThresholdLabel ? (
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#383838",
                }}
              >
                {meetsTarget && !hasGapFailures ? "Expected" : "Reference"}: {targetThresholdLabel}
              </Typography>
            ) : null}

            {scoreDeviationBelow != null ? (
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#C01919",
                }}
              >
                Score deviation: {scoreDeviationBelow}% below target
              </Typography>
            ) : null}

            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontSize: 14,
                fontWeight: 400,
                lineHeight: "21px",
                color: "#555555",
                maxWidth: 520,
              }}
            >
              {overallScore == null
                ? "Complete execution to compute an overall score."
                : thresholdSummary}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            border: "1px solid #E6E6E6",
            borderRadius: "12px",
            bgcolor: "#FFFFFF",
            p: { xs: 2, md: 3 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            boxSizing: "border-box",
          }}
        >
          <Box>
            <Typography
              component="h3"
              id="gate2-decision-vote-heading"
              sx={{
                fontFamily: "Lato, sans-serif",
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: "-0.3px",
                color: "#383838",
              }}
            >
              Your Approval
            </Typography>
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontSize: 14,
                fontWeight: 500,
                color: "#555555",
                mt: 1,
              }}
            >
              This attestation is recorded in the audit trail and cannot be undone.
            </Typography>
          </Box>

          <Box
            component="div"
            role="radiogroup"
            aria-labelledby="gate2-decision-vote-heading"
            sx={{ display: "flex", flexDirection: "column", gap: 1 }}
          >
            {VOTE_OPTIONS.map((option) => (
              <VoteOptionRow
                key={option.value}
                option={option}
                selected={vote}
                disabled={option.value === "go" && goVoteDisabled}
                onSelect={onVoteChange}
              />
            ))}
          </Box>

          <Box sx={{ py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0, mb: 1 }}>
              {rationaleRequired ? (
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontSize: 14,
                    fontWeight: 700,
                    lineHeight: "21px",
                    color: "#DE350B",
                  }}
                >
                  *
                </Typography>
              ) : null}
              <Typography
                component="label"
                htmlFor="gate2-decision-rationale"
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: "21px",
                  color: "#707070",
                }}
              >
                Rationale
              </Typography>
            </Box>
            <TextField
              id="gate2-decision-rationale"
              multiline
              minRows={3}
              fullWidth
              placeholder="Explain your decision..."
              value={rationale}
              onChange={(e) => onRationaleChange(e.target.value.slice(0, RATIONALE_MAX))}
              inputProps={{
                "aria-required": rationaleRequired ? true : undefined,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Lato, sans-serif",
                  fontSize: 16,
                  lineHeight: "24px",
                  borderRadius: "4px",
                  bgcolor: "#FFFFFF",
                  boxShadow: "inset 0px 2px 3px 1px rgba(85, 85, 85, 0.2)",
                  "& fieldset": { borderColor: "#707070" },
                  "&:hover fieldset": { borderColor: "#707070" },
                  "&.Mui-focused fieldset": { borderColor: "#707070", borderWidth: 1 },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#707070",
                  opacity: 1,
                },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mt: 1 }}>
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: "#707070",
                }}
              >
                {rationaleRequired
                  ? "Required when overall score is below the target trust index."
                  : "Optional — add context for the audit trail if you wish."}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: "#707070",
                }}
              >
                {rationale.length}/{RATIONALE_MAX}
              </Typography>
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Gate2DecisionCheckbox
                checked={attestationChecked}
                onChange={(e) => onAttestationChange(e.target.checked)}
              />
            }
            label={
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: "#383838",
                }}
              >
                I confirm all evidence is accurate and complete to the best of my knowledge.
              </Typography>
            }
            sx={{ alignItems: "center", gap: 1, m: 0, ml: 0 }}
          />
        </Box>

        <Box
          sx={{
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            bgcolor: "#FFFFFF",
            p: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Lato, sans-serif",
              fontSize: 20,
              fontWeight: 900,
              lineHeight: "27px",
              color: "#383838",
            }}
          >
            Handoff Actions
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2.5,
              pb: 2,
            }}
          >
            <HandoffCard
              icon={<DescriptionOutlinedIcon sx={{ fontSize: 20, color: "#383838" }} />}
              title="Download Compliance Report (PDF)"
              description="Download the full evidence package as a signed compliance PDF."
            />
            <HandoffCard
              icon={<NotificationsNoneOutlinedIcon sx={{ fontSize: 20, color: "#383838" }} />}
              title="Notify Release Team"
              description="Send gate approval notification to the release management channel."
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function canSubmitGate2Decision({ vote, rationale, attestationChecked, meetsTarget, hasGapFailures }) {
  if (!attestationChecked || !vote) return false;
  if (vote === "go") {
    return meetsTarget && !hasGapFailures;
  }
  if (vote === "conditional_go" || vote === "no_go") {
    return rationale.trim().length > 0;
  }
  return false;
}
